// src/pages/Team/OwnerSnapshot.jsx
// STATUS: active | UPDATED: 2026-08-30
// LOG:
//   2026-08-30  New. An owner is asking a different question from a manager.
//
// A manager opens this product to find out who is waiting on them. An owner
// opens it to find out whether the store is getting better and whether the
// person they pay to run the floor is running it.
//
// Until now they were shown the same screen, which answered the first question
// and not the second.
//
// THE NUMBER AN OWNER ACTUALLY NEEDS IS SIGN OFFS
//
// Not logins, not steps started, not coach messages. Those all measure the
// floor. Sign offs measure the manager, because a sign off is the one action in
// this product that only a manager can take and that requires them to stand
// next to somebody and watch.
//
// A store where salespeople are working through modules and nobody is signing
// anything off looks healthy on every other number and is not. It is the
// leading indicator of a store that cancels in sixty days, and an owner is the
// only person who can do anything about it.
//
// SAID AS FACT, NOT AS ACCUSATION
//
// "Nothing has been signed off in the last 7 days" is a true sentence an owner
// can take to a conversation. "Your manager is not doing their job" is a
// conclusion that is not ours to draw, and would be wrong the week somebody was
// on holiday. The screen reports and stops.

import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Icon, Spinner, SimpleGrid, Link } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Building2, ClipboardCheck, TrendingUp, Gauge } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { STEPS } from '../../content/steps'

const DAY = 24 * 60 * 60 * 1000

function Stat({ label, value, note, accent = false }) {
  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 4, md: 5 }}>
      <Text textStyle="eyebrow" color={accent ? 'accent.500' : 'inkMuted'} mb={2}>
        {label}
      </Text>
      <Text fontSize="display-sm" fontWeight={600} color="ink" lineHeight={1.1} letterSpacing="-0.02em">
        {value}
      </Text>
      {note && (
        <Text fontSize="body-sm" color="inkMuted" mt={1.5} lineHeight={1.5}>
          {note}
        </Text>
      )}
    </Box>
  )
}

export default function OwnerSnapshot({ members }) {
  const [state, setState] = useState({ loading: true, data: null })

  const load = useCallback(async () => {
    const since = new Date(Date.now() - 7 * DAY).toISOString()

    // RLS scopes every one of these to this owner's own dealership, which is
    // why there is no role check and no dealership filter written here.
    const [signoffRes, progressRes, drillRes, reportRes] = await Promise.all([
      supabase.from('manager_signoffs').select('status, decided_at, manager_id, created_at'),
      supabase.from('step_progress').select('user_id, status'),
      supabase.from('activity_events').select('user_id').eq('kind', 'practice_drill').gte('created_at', since),
      supabase
        .from('storefront_reports')
        .select('overall, share_token, created_at')
        .order('created_at', { ascending: false })
        .limit(1),
    ])

    if (signoffRes.error || progressRes.error) {
      setState({ loading: false, data: null })
      return
    }

    const signoffs = signoffRes.data || []
    const approvedThisWeek = signoffs.filter(
      (s) => s.status === 'approved' && s.decided_at && s.decided_at >= since
    ).length
    const waiting = signoffs.filter((s) => s.status === 'requested').length

    const progress = progressRes.data || []
    const signedOff = progress.filter((p) => p.status === 'signed_off').length
    const people = (members || []).filter((m) => m.role !== 'owner').length
    const total = people * STEPS.length

    setState({
      loading: false,
      data: {
        approvedThisWeek,
        waiting,
        signedOff,
        total,
        pct: total > 0 ? Math.round((signedOff / total) * 100) : 0,
        // NULL RATHER THAN ZERO WHEN THE READ FAILED. "Drills this week: 0"
        // is a statement about whether the floor practised, and a failed
        // query is not entitled to make it. The two reads above are already
        // guarded, and these two were not.
        drills: drillRes.error ? null : (drillRes.data || []).length,
        report: reportRes.error ? null : (reportRes.data || [])[0] || null,
      },
    })
  }, [members])

  useEffect(() => {
    load()
  }, [load])

  if (state.loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner size="md" color="ink" thickness="3px" />
      </Flex>
    )
  }
  if (!state.data) return null

  const d = state.data
  // Somebody is queued and nothing has been approved all week. That is the one
  // pattern worth putting in front of an owner.
  const stalled = d.waiting > 0 && d.approvedThisWeek === 0

  return (
    <VStack align="stretch" spacing={3}>
      <HStack spacing={2}>
        <Icon as={Building2} boxSize={4} color="inkMuted" />
        <Text textStyle="eyebrow" color="inkMuted">
          Your store, last 7 days
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Stat
          label="Signed off"
          value={d.approvedThisWeek}
          accent={stalled}
          note={
            d.approvedThisWeek === 0
              ? 'Nothing approved this week'
              : `${d.approvedThisWeek} step${d.approvedThisWeek === 1 ? '' : 's'} approved by a manager`
          }
        />
        <Stat
          label="Queued"
          value={d.waiting}
          accent={stalled}
          note={d.waiting === 0 ? 'Nobody is waiting' : 'Passed the check, waiting on a manager'}
        />
        <Stat
          label="Store progress"
          value={`${d.pct}%`}
          note={`${d.signedOff} of ${d.total} steps signed off`}
        />
        <Stat
          label="Drills run"
          value={d.drills}
          note={d.drills === 0 ? 'Nobody has practised' : 'Practice nobody was told to do'}
        />
      </SimpleGrid>

      {stalled && (
        <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={3} align="flex-start">
          <Box color="accent.500" flexShrink={0} pt={0.5}>
            <ClipboardCheck size={16} />
          </Box>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} maxW="measure">
            <Text as="span" color="ink" fontWeight={600}>
              {d.waiting} {d.waiting === 1 ? 'person is' : 'people are'} waiting to be signed off and
              nothing has been approved in the last 7 days.
            </Text>{' '}
            They have proved they know the step. What is left is somebody watching them do it with a
            customer. That part only happens on the floor.
          </Text>
        </HStack>
      )}

      {d.report && (
        <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={4} align="center" wrap="wrap">
          <Icon as={Gauge} boxSize={4} color="inkMuted" />
          <Text fontSize="display-sm" fontWeight={600} color="ink" lineHeight={1} fontVariantNumeric="tabular-nums">
            {d.report.overall}
          </Text>
          <Box flex={1} minW="180px">
            <Text fontSize="body-sm" color="ink" fontWeight={600}>
              Your storefront score
            </Text>
            <Text fontSize="12px" color="inkMuted">
              How your website reads to a customer and to Google
            </Text>
          </Box>
          <Link
            as={RouterLink}
            to={`/report/${d.report.share_token}/`}
            fontSize="12px"
            color="accent.500"
            fontWeight={600}
            display="inline-flex"
            alignItems="center"
            gap={1}
          >
            Read the report <TrendingUp size={12} />
          </Link>
        </HStack>
      )}
    </VStack>
  )
}
